import { Controller } from "@hotwired/stimulus"

// Inserts canned response content into the Lexxy editor that belongs to the
// same <form> as the picker. The ticket page also renders the description's
// <lexxy-editor> in another form, so the lookup is scoped to `closest("form")`.
// data-controller="support--canned-response"
export default class extends Controller {
  static targets = ["list", "trigger"]

  connect() {
    this._boundClose = this._closeOnClickOutside.bind(this)
    document.addEventListener("click", this._boundClose)
  }

  disconnect() {
    document.removeEventListener("click", this._boundClose)
  }

  insert(event) {
    event.preventDefault()
    const html = event.currentTarget.dataset.content

    const editor = this.element.closest("form")?.querySelector("lexxy-editor")
    const editable = editor?.querySelector("[contenteditable]")
    if (editable) {
      editable.focus()
      // The stored content is HTML from the rich editor. Lexxy parses it into
      // editor nodes at the cursor, so paragraphs and lists keep their shape
      // (CONEK-7655); an editor without attachments drops attachment nodes.
      // Empty edge blocks take the paste-style merge, so the response never glues onto text around the caret.
      // `contents.insertHtml` is not in Lexxy's README:
      // test/system/support/canned_response_insert_test.rb pins it.
      const prefix = textAroundCaret(editable, "before").trim() ? "<p></p>" : ""
      const suffix = textAroundCaret(editable, "after").trim() ? "<p></p>" : ""
      editor.contents.insertHtml(prefix + html + suffix)
    }

    this.listTarget.classList.add("hidden")
  }

  toggle(event) {
    event.stopPropagation()
    this.listTarget.classList.toggle("hidden")
  }

  _closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.listTarget.classList.add("hidden")
    }
  }
}

// Lexxy's quotes hold paragraphs, so `p` matches first; `blockquote` covers a
// quote without them. Code blocks render as <code spellcheck="false">; inline
// code is a bare <code> inside a paragraph and must not match.
const BLOCK_SELECTOR =
  "p, li, h1, h2, h3, h4, h5, h6, blockquote, code[spellcheck], pre"

// Text between the caret and its block's start ("before") or end ("after").
function textAroundCaret(editable, side) {
  const selection = window.getSelection()
  if (!selection?.rangeCount) return ""

  const caret = selection.getRangeAt(0)
  // A selection can span blocks: measure each side from its own end of it.
  const container =
    side === "before" ? caret.startContainer : caret.endContainer
  if (!editable.contains(container)) return ""

  const element =
    container.nodeType === Node.ELEMENT_NODE
      ? container
      : container.parentElement
  const block = element?.closest(BLOCK_SELECTOR)
  if (!block || !editable.contains(block)) return ""

  const range = document.createRange()
  if (side === "before") {
    range.setStart(block, 0)
    range.setEnd(caret.startContainer, caret.startOffset)
  } else {
    range.setStart(caret.endContainer, caret.endOffset)
    range.setEnd(block, block.childNodes.length)
  }
  return range.toString()
}
